import { useCallback, useEffect, useMemo, useState } from 'react';
import { useGlobal } from 'reactn';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import { LOCATIONS } from '../../../constants';
import { EVENTS } from '../../services/constants';
import { eventEmitter } from '../../services/event-emitter-service';
import { REENTRY_PLANNING_TABS } from './constants/reentry-planning.constants';
import {
  hasCasePlanAccess,
  resolveEntitledPlanTypesFromLocations,
} from './utils/reentry-planning.util';

export const ACCESS_STATUS = {
  PENDING: 'pending',
  GRANTED: 'granted',
  DENIED: 'denied',
};

export const useReentryPlanningModuleHook = () => {
  const [global] = useGlobal();
  const [accessStatus, setAccessStatus] = useState(ACCESS_STATUS.PENDING);
  const [activeTab, setActiveTab] = useState(REENTRY_PLANNING_TABS.ALL_PLANS);
  const [lastRefreshed, setLastRefreshed] = useState(() => new Date());
  const [locationsRevision, setLocationsRevision] = useState(0);

  const entitledPlanTypes = useMemo(
    () => resolveEntitledPlanTypesFromLocations(LOCATIONS),
    // LOCATIONS is module-level; `locationsRevision` bumps when it is populated (LOCATIONS_FETCHED).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional invalidation, not a missing dep on LOCATIONS
    [locationsRevision],
  );

  const tabs = [
    {
      label: REENTRY_PLANNING_TABS.ALL_PLANS,
      value: REENTRY_PLANNING_TABS.ALL_PLANS,
      Icon: MeetingRoomIcon,
    },
  ];

  const checkAccess = useCallback(() => {
    if (!hasCasePlanAccess(global.permissions, global.permissionIds)) {
      setAccessStatus(ACCESS_STATUS.DENIED);
      return;
    }

    if (global.isSuperAdmin) {
      setAccessStatus(ACCESS_STATUS.GRANTED);
      return;
    }

    if (resolveEntitledPlanTypesFromLocations(LOCATIONS).length > 0) {
      setAccessStatus(ACCESS_STATUS.GRANTED);
      return;
    }

    setAccessStatus(ACCESS_STATUS.DENIED);
  }, [global.permissions, global.permissionIds, global.isSuperAdmin]);

  useEffect(() => {
    const handleLocationsFetched = () => {
      setLocationsRevision((revision) => revision + 1);
      checkAccess();
    };

    eventEmitter.on(EVENTS.LOCATIONS_FETCHED, handleLocationsFetched);
    return () => {
      eventEmitter.off(EVENTS.LOCATIONS_FETCHED, handleLocationsFetched);
    };
  }, [checkAccess]);

  useEffect(() => {
    checkAccess();
  }, [checkAccess]);

  const onTabChange = useCallback((_event, value) => {
    setActiveTab(value);
  }, []);

  const refreshPlans = useCallback(() => {
    setLastRefreshed(new Date());
  }, []);

  return {
    accessStatus,
    activeTab,
    entitledPlanTypes,
    lastRefreshed,
    onTabChange,
    refreshPlans,
    tabs,
  };
};
